import { createContext, type FC, type PropsWithChildren, useContext } from "react";

import { ErrorView } from "@/components/_molecules/error-view";
import { Loading } from "@/components/_molecules/loading-view";
import type { GetInventoryQuery } from "@/graphql/generated/graphql";

import { useGetInventory } from "./api/use-get-inventory";

type CurrentInventoryContext = {
  currentInventory: GetInventoryQuery["inventory"];
  hasRefreshError?: boolean;
};

const CurrentInventoryContext = createContext<CurrentInventoryContext | null>(null);

export interface CurrentInventoryContextProviderProps extends PropsWithChildren {
  inventoryId: string;
}

export const CurrentInventoryContextProvider: FC<CurrentInventoryContextProviderProps> = ({
  children,
  inventoryId,
}) => {
  const { inventory, loading, error } = useGetInventory({ id: inventoryId });

  if (loading) {
    return <Loading />;
  }

  if (error && !inventory) {
    return <ErrorView />;
  }

  if (!inventory) {
    return <ErrorView />;
  }

  return (
    <CurrentInventoryContext.Provider value={{ currentInventory: inventory, hasRefreshError: Boolean(error) }}>
      {children}
    </CurrentInventoryContext.Provider>
  );
};

export const useCurrentInventoryContext = () => {
  const context = useContext(CurrentInventoryContext);

  if (!context) {
    throw new Error("useCurrentInventoryContext must be used within a CurrentInventoryContextProvider");
  }

  return context;
};

export const useUnsafeCurrentInventoryContext = (): CurrentInventoryContext | null => {
  return useContext(CurrentInventoryContext);
};
